import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Lightning, SignOut, UserCircle, ShieldCheck, ShoppingCartSimple, BookOpen } from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isInternal = location.pathname.startsWith("/library") ||
                     location.pathname.startsWith("/tools/") ||
                     location.pathname.startsWith("/courses/") ||
                     location.pathname.startsWith("/admin");

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
          <div className="w-7 h-7 bg-primary flex items-center justify-center rounded-sm group-hover:animate-pulse-glow">
            <Lightning size={18} weight="fill" className="text-black" />
          </div>
          <span className="font-display font-black text-lg tracking-tight">
            STOCK<span className="text-primary">TOOLS</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
          <Link to="/store" data-testid="nav-store" className="hover:text-primary transition-colors">Store</Link>
          {user && <Link to="/library" data-testid="nav-library" className="hover:text-primary transition-colors">My Library</Link>}
          {user?.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin" className="hover:text-primary transition-colors flex items-center gap-1">
              <ShieldCheck size={14} weight="bold" /> Admin
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/cart" data-testid="cart-btn" className="relative flex items-center gap-1 px-3 py-1.5 border border-border hover:border-primary/50 transition-colors rounded-sm">
            <ShoppingCartSimple size={16} weight="duotone" />
            <span className="text-xs font-mono">Cart</span>
            {count > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-primary text-black text-xs font-bold rounded-full flex items-center justify-center" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 px-3 py-1.5 border border-border hover:border-primary/50 transition-colors rounded-sm">
                  <UserCircle size={18} weight="duotone" />
                  <span className="text-sm font-mono hidden sm:inline">{user.email.split("@")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/library")} data-testid="menu-library"><BookOpen size={14} weight="bold" className="mr-2" /> My Library</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/store")} data-testid="menu-store">Browse Store</DropdownMenuItem>
                {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">Admin Panel</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-destructive"><SignOut size={14} weight="bold" className="mr-2" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login"><Button variant="ghost" size="sm" data-testid="header-login-btn" className="rounded-sm">Sign in</Button></Link>
              <Link to="/register"><Button size="sm" data-testid="header-register-btn" className="bg-primary text-black hover:bg-primary/90 rounded-sm font-bold">Get Started</Button></Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
